<script setup lang="ts">
import { ref, computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import AppButton from '@uiKit/elements/Button.vue'
import Text from '@uiKit/elements/Text.vue'
import type { SlicealAccount } from '../types/fiatWalletTypes'

const COPY_FEEDBACK_MS = 2000

interface Props {
  amount: string
  slicealAccount?: SlicealAccount | null
}

const props = withDefaults(defineProps<Props>(), {
  slicealAccount: null,
})

defineEmits<{
  (e: 'back'): void
  (e: 'made-transfer'): void
}>()

const ibanCopied = ref(false)

const bankDetails = computed(() => {
  const account = props.slicealAccount
  if (!account) {
    return { accountName: '—', bankName: '—', iban: '—', swift: '—' }
  }
  return {
    accountName: account.accountName,
    bankName: account.bankName,
    iban: account.iban,
    swift: account.swiftBic,
  }
})

const copyIban = async () => {
  await navigator.clipboard.writeText(bankDetails.value.iban)
  ibanCopied.value = true
  setTimeout(() => (ibanCopied.value = false), COPY_FEEDBACK_MS)
}
</script>

<template>
  <Box direction="col" gap="4" class="dbts">
    <Box direction="col" gap="4" class="dbts__body">
    <Box direction="col" gap="0" class="dbts__card">
      <Box align="center" justify="between" gap="3" class="dbts__amount-row">
        <Box direction="col" gap="1">
          <Text type="micro" color="muted" is="span">Transfer this amount</Text>
          <Text type="title-lg" weight="bold" is="span">AED {{ amount }}</Text>
        </Box>
        <AppButton
          label="Change"
          variant="text"
          color="primary"
          size="small"
          @click="$emit('back')"
        />
      </Box>

      <Box direction="col" gap="3" class="dbts__bank">
        <Box align="center" justify="between" gap="3">
          <Text type="body-sm" color="muted" is="span">Account name</Text>
          <Text type="body-sm" weight="semibold" align="right" is="span">
            {{ bankDetails.accountName }}
          </Text>
        </Box>
        <Box align="center" justify="between" gap="3">
          <Text type="body-sm" color="muted" is="span">Bank</Text>
          <Text type="body-sm" weight="semibold" align="right" is="span">
            {{ bankDetails.bankName }}
          </Text>
        </Box>
        <Box align="center" justify="between" gap="3">
          <Text type="body-sm" color="muted" is="span">SWIFT / BIC</Text>
          <Text type="body-sm" weight="semibold" align="right" is="span">
            {{ bankDetails.swift }}
          </Text>
        </Box>
        <Box direction="col" gap="1">
          <Box align="center" justify="between" gap="3">
            <Text type="body-sm" color="muted" is="span">IBAN</Text>
            <AppButton
              :label="ibanCopied ? 'Copied' : 'Copy'"
              :icon="ibanCopied ? 'check' : 'copy'"
              variant="text"
              color="primary"
              size="small"
              @click="copyIban"
            />
          </Box>
          <Text type="body" weight="semibold" is="span" class="dbts__iban-value">
            {{ bankDetails.iban }}
          </Text>
        </Box>
      </Box>
    </Box>

    <Text type="caption" color="muted" align="center" is="p" class="dbts__hint">
      Send exactly this amount from your banking app, then tap below.
      Funds reflect in 1–2 working days.
    </Text>
    </Box>

    <Box class="dbts__spacer" />

    <Box direction="col" gap="3" class="dbts__actions">
      <AppButton
        label="I've Made the Transfer"
        block
        size="large"
        @click="$emit('made-transfer')"
      />
      <AppButton label="Back" variant="text" color="secondary" block @click="$emit('back')" />
    </Box>

    <Box align="center" justify="center" gap="2" class="dbts__trust">
      <PiIcon name="shield" style="font-size: 14px; color: var(--p-success-600)" />
      <Text type="caption" color="muted" is="span">
        Held in escrow with Emirates NBD &middot; VARA-compliant
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.dbts {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1.25rem 1.5rem 1.5rem;
  box-sizing: border-box;
}

.dbts__body {
  flex-shrink: 0;
}

.dbts__spacer {
  flex: 1;
  min-height: 1rem;
}

.dbts__card {
  background: var(--p-surface-50);
  border-radius: var(--p-border-radius-md);
  overflow: hidden;
}

.dbts__amount-row {
  background: var(--p-primary-50);
  padding: 0.875rem 1rem;
}

.dbts__bank {
  padding: 0.875rem 1rem 1rem;
}

.dbts__iban-value {
  font-variant-numeric: tabular-nums;
  word-break: break-all;
}

.dbts__hint {
  margin: 0;
  line-height: 1.55;
}

.dbts__actions {
  flex-shrink: 0;
}

.dbts__trust {
  flex-shrink: 0;
  text-align: center;
}
</style>
